import { useState } from "react";
import validator from "validator";
import { useDebounce } from "use-debounce";
import { keepPreviousData } from "@tanstack/react-query";
import { MultibankingApiProvider } from "@api";
import { useGetAuthorization } from "@rentablo/api/apiv1";
import { useBanks } from "@rentablo/api/apiv1";
import { UserAuthorization } from "@rentablo/api/apiv1";
import { useRoutes } from "../../../../../hooks/useRoutes";
import { groupBankByName } from "../utils";

const hasFinApiAccount = (authorization: UserAuthorization) =>
  authorization.thirdPartyAccounts?.includes("FINAPI");

interface Options {
  defaultSearchValue?: string;
  onSearchChange?: (value: string) => void;
}

/**
 * Encapsulates the data fetching and bank-selection navigation that is shared
 * between the legacy bank-select page and the AddPortfolioModal bank step.
 */
export const useImportBankSelect = ({
  defaultSearchValue = "",
  onSearchChange,
}: Options = {}) => {
  const { pushLegalNote, pushImportConnection } = useRoutes();
  const { data: authorization } = useGetAuthorization();

  const [searchValue, setSearchValue] = useState(defaultSearchValue);

  const handleChange = (value: string) => {
    setSearchValue(value);
    onSearchChange?.(value);
  };

  const [value] = useDebounce(searchValue, 300);

  const valueWithoutSpaces =
    value && validator.isIBAN(value) ? value.replace(/\s/g, "") : value;

  const { data, isFetching } = useBanks(
    { search: valueWithoutSpaces },
    { placeholderData: keepPreviousData }
  );

  const banks = groupBankByName(data?.banks);

  const onSelect = (
    id: number | undefined,
    multibankingProvider: MultibankingApiProvider,
    wealthApiSlug?: string
  ) => {
    if (!authorization) return;
    hasFinApiAccount(authorization)
      ? pushImportConnection(id, multibankingProvider, wealthApiSlug)
      : pushLegalNote(id, multibankingProvider, wealthApiSlug);
  };

  return {
    banks,
    isFetching,
    searchValue,
    onChange: handleChange,
    onSelect,
  };
};
